/**
 * useTypingEmit — Spec 66 Phase F
 *
 * Shared helper for composing components that need to emit
 * typing.start / typing.stop events to the server.
 *
 * Usage:
 *   const { onKeystroke, onSendOrBlur } = useTypingEmit(conversationId, enabled);
 *
 * Rules:
 *  - onKeystroke() fires typing.start on the FIRST keystroke, then is a no-op
 *    until onSendOrBlur() is called (avoids rate-limit spam).
 *  - A 500ms debounce sends typing.stop automatically after inactivity.
 *  - onSendOrBlur() always sends typing.stop and resets state.
 *  - No network call is made if conversationId is null or enabled=false.
 *
 * The typing endpoint is `POST /conversations/:id/events/typing` with body
 * `{ typing: boolean }` (per types.gen.ts ConversationTypingRequest).
 */
import { useRef, useCallback, useEffect } from 'react';
import { setConversationTyping } from '../generated/api/sdk.gen.js';

const STOP_DEBOUNCE_MS = 500;

export function useTypingEmit(
  conversationId: string | number | null | undefined,
  enabled: boolean,
): {
  onKeystroke: () => void;
  onSendOrBlur: () => void;
} {
  const isTypingRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const convIdStr = conversationId != null ? String(conversationId) : null;

  const emitStop = useCallback(() => {
    if (!convIdStr || !enabled) return;
    if (!isTypingRef.current) return;
    isTypingRef.current = false;
    // Fire-and-forget — ignore errors (network hiccup shouldn't block the UI).
    void setConversationTyping({
      path: { id: convIdStr },
      body: { typing: false },
    }).catch(() => {
      /* swallow */
    });
  }, [convIdStr, enabled]);

  const emitStart = useCallback(() => {
    if (!convIdStr || !enabled) return;
    if (isTypingRef.current) return; // already marked typing
    isTypingRef.current = true;
    void setConversationTyping({
      path: { id: convIdStr },
      body: { typing: true },
    }).catch(() => {
      /* swallow */
    });
  }, [convIdStr, enabled]);

  const onKeystroke = useCallback(() => {
    if (!convIdStr || !enabled) return;

    emitStart();

    // Reset the stop debounce timer on every keystroke.
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      emitStop();
    }, STOP_DEBOUNCE_MS);
  }, [convIdStr, enabled, emitStart, emitStop]);

  const onSendOrBlur = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    emitStop();
  }, [emitStop]);

  // Cleanup on unmount — send stop if still typing.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      emitStop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convIdStr]);

  return { onKeystroke, onSendOrBlur };
}
